import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { Train } from "../../../trains/models/train";
import { TrainDataService } from '../../../trains/services/train-data.service';
import { Observable, of, Subscription } from "rxjs";
import { Statistic, Trip } from "../../models/trip-overview";
import { TripOverviewService } from "../../services/trip-overview.service";
import { map, tap } from "rxjs/operators";
import { StateService } from "@uirouter/core";
import { ReportingDataService } from "../../services/reporting-data.service";
import { DatePipe } from "@angular/common";
import { ParseUrlDateStringToDatePipe } from "../../../../shared/pipes/parse-url-date-string-to-date.pipe";
import { TranslateService } from "@ngx-translate/core";
import { ProjectConfiguration, SqiConfiguration } from "../../../project/models/project-configuration";
import { ProjectConfigurationBehaviorSubjectService } from "../../../project/services/project-configuration-behavior-subject.service";
import { SlaDocumentationComponent } from "../../components/sla-documentation/sla-documentation.component";
import { QueryWordsDataService } from '../../services/query-words-data.service';
import { MessageService } from "primeng/api";
import { ResponseText } from "@/app/shared/models/shared.model";
import { TrainsSelectorFormComponent } from '../../components/trains-selector-form/trains-selector-form.component';
import { UserProfileService } from '../../../users/services/user-profile.service';
import { TripDurationPipe } from '../../pipes/trip-duration.pipe';
import { TableStateService } from '../../../../shared/services/table-state.service';


type tripWithStat = Trip & {[key: string]: number} & { duration: string}

@Component({
  selector: 'app-trip-overview',
  template: require('./trip-overview.component.html'),
  styles: [require('./trip-overview.component.sass')]
})
export class TripOverviewComponent implements OnInit {

  @ViewChild(TrainsSelectorFormComponent) trainsSelectorForm: TrainsSelectorFormComponent;
  @ViewChild(SlaDocumentationComponent) slaDocumentation: SlaDocumentationComponent;

  trains$: Observable<Train[]>;
  trainTypes$: Observable<string[]>;
  maintainers$: Observable<string[]>;
  trips$: Observable<Trip[] | null>;
  tripsWithStats$: Observable<tripWithStat[] | null>;
  averagedSQIs$: Observable<Statistic[] | null>;

  projectId: number;
  query: string = '';
  words: any;
  from: Date | undefined;
  to: Date | undefined;
  goodTrips: boolean;

  projectConfigurationSubscription: Subscription;
  projectConfiguration: ProjectConfiguration;
  sqiConfigurations: SqiConfiguration[];
  trainIds: string[];
  tripOverviewDateRange: number;

  languageChangeSubscription: Subscription;

  tableConfiguration: any = {
    paginate: true,
    sortableMode: 'multiple',
    defaultPagination: 50,
    itemsPerPageOptions: [50, 100, 250, 500],
    globalFilterFields: ['serviceId', 'trainName', 'trainId', 'trajectory', 'departure', 'arrival'],
  }
  columns: any[];

  selectedTrips: Trip[];

  showHideSchedulingErrorsCheckbox: boolean;

  constructor(
      @Inject ( 'ConfigurationStoreService' ) private configurationStoreService: any,
      @Inject ( 'TripOverviewFactory' ) private tripOverviewFactory: any,
      private projectConfigurationBehaviorSubjectService: ProjectConfigurationBehaviorSubjectService,
      private $state: StateService,
      private trainDataService: TrainDataService,
      private reportingDataService: ReportingDataService,
      private parseUrlDateStringToDatePipe: ParseUrlDateStringToDatePipe,
      private datePipe: DatePipe,
      private tripsOverviewService: TripOverviewService,
      private translate: TranslateService,
      private queryWordsDataService: QueryWordsDataService,
      private messageService: MessageService,
      public userProfileService: UserProfileService,
      private tripDuration: TripDurationPipe,
      public tableState: TableStateService
  ) {
    //Get SQI columns from Project Config
    this.projectConfigurationSubscription = this.projectConfigurationBehaviorSubjectService.changeOccured.subscribe(() => {
      this.projectConfiguration = this.configurationStoreService.getProjectConfiguration(this.projectId);
      this.sqiConfigurations = this.projectConfiguration && this.projectConfiguration.sqiConfigurations;
      this.showHideSchedulingErrorsCheckbox = this.projectConfiguration && this.projectConfiguration.showHideSchedulingErrorsCheckbox;
      this.tripOverviewDateRange = this.projectConfiguration && this.projectConfiguration.periodRange && this.projectConfiguration.periodRange.tripOverviewRange;
      this.setColumns();
    });
    this.languageChangeSubscription = this.translate.onLangChange.subscribe(() => {
      this.setColumns();
    })
  }

  ngOnInit(): void {
    this.tripOverviewFactory.clearList();
    this.projectId = this.$state.params && this.$state.params.projectId;
    this.query = this.$state.params && this.$state.params.query || '';
    this.from = this.$state.params && this.$state.params.from  && this.parseUrlDateStringToDatePipe.transform(this.$state.params.from);
    this.to = this.$state.params && this.$state.params.to  && this.parseUrlDateStringToDatePipe.transform(this.$state.params.to);
    this.trainIds = this.$state.params && this.$state.params.trainIds && this.$state.params.trainIds.split(",");
    this.goodTrips = this.$state.params && (this.$state.params.goodTrips === "true" || (this.$state.params.goodTrips === null));
    this.trains$ = this.trainDataService.getTrainslist(this.projectId);
    this.trainTypes$ = this.trains$.pipe(map(trains => {
      return [...new Set(trains.map(train => {
        return train.displayTypeName || train.trainType;
      }))]

    }));
    this.maintainers$ = this.trains$.pipe(map(trains => [...new Set(trains.map(train => train.maintainer))]));
    this.queryWordsDataService.getWords(this.projectId).then(words => {
      this.words = words
    });
    const fromFormatted = this.datePipe.transform(this.from, "yyyy-MM-dd'T'HH:mm:ssZ");
    const toFormatted = this.datePipe.transform(this.to, "yyyy-MM-dd'T'HH:mm:ssZ");
    if (fromFormatted && toFormatted) {
      this.trips$ = this.trainIds ? this.reportingDataService.getTrips(
          this.projectId,
          fromFormatted,
          toFormatted,
          this.trainIds.reduce<string>((acc, id) => acc ? acc + "," + id.toString() : id.toString(), ''),
          this.goodTrips ? (this.query ? this.query.concat(" AND LATENCY > -1") : "LATENCY > -1") : this.query ? this.query : "").pipe(
          tap(trips => trips.forEach(trip => this.tripsOverviewService.prepareTripData(trip, this.sqiConfigurations)))
      ) : of(null);

      this.averagedSQIs$ = this.trips$.pipe(map(trips => trips && trips.length > 0 ? this.tripsOverviewService.getAverageSQIs(trips, this.sqiConfigurations) : null));
      this.tripsWithStats$ = this.trips$.pipe(map(trips => {
        return trips ? trips.map(trip => {
          const statistics = trip.statistics;
          return <tripWithStat>{
            ...trip,
            duration: this.tripDuration.transform(trip.arrival - trip.departure),
            ...statistics.reduce((acc: {[key: string]: number}, stat) => {
              const sqiConfiguration = this.sqiConfigurations.find(sqi => sqi.id === stat.sqiId);
              if (sqiConfiguration) {
                acc[sqiConfiguration.label] = stat.value
              }
              return acc;
            }, {})
          }
        }) : []
      }))
    }
  }

  onSubmitRequest = (data: {
    trains: Train[],
    from: string,
    to: string,
    hideSchedulingErrors: boolean
  }) => {

    this.$state.go('trips-overview', {
      projectId: this.projectId,
      from: data.from,
      to: data.to,
      trainIds: data.trains.reduce<string>((acc, train) => acc ? acc + (train.id ? ("," + train.id.toString()) : '') : (train.id ? train.id.toString() : ''), ''),
      goodTrips: data.hideSchedulingErrors,
      query: this.query
    }, {reload: true})
  }

  openDocumentation = async (fileName: string) => {
    await this.slaDocumentation.showModal(this.projectId, fileName);
  }

  recalculateTrips = async () => {
    if (!this.selectedTrips || (this.selectedTrips.length === 0)) {
      this.messageService.add({
        severity:'error',
        summary: 'No trips selected',
        detail: 'Please select 1 or more trips to be recalculated'
      });
      return;
    }

    this.displayRecalculationMessage(await this.reportingDataService.recalculateTrips(
        this.projectId,
        this.selectedTrips.map(trip => trip.id).join(',')));
  }

  recalculateTripsByTrainIds = async () => {
    if (!this.to || !this.from) {
      return
    } else if (this.to <= this.from) {
      this.messageService.add({
        severity:'error',
        summary: 'Invalid dates selected',
        detail: 'The selected "To" date is set before the selected "From" date, no trips can be found'
      });
      return;
    }

    const selectedTrains = <Train[]>this.trainsSelectorForm.reportingForm.value.trains || [];
    const response: ResponseText = await this.reportingDataService.recalculateTripsByTrainIds(
      this.projectId,
      this.datePipe.transform(this.to, "yyyy-MM-dd") || '',
      this.datePipe.transform(this.from, "yyyy-MM-dd") || '',
      selectedTrains.reduce<string>((acc, train) => acc ? acc + (train.relativeId ? ("," + train.relativeId.toString()) : '') : (train.relativeId ? train.relativeId.toString() : ''), '')
    )

    this.displayRecalculationMessage(response);
  }

  calculateTripsBySchedule = async () => {
    if (!this.to || !this.from) {
      return
    } else if (this.to <= this.from) {
      this.messageService.add({
        severity:'error',
        summary: 'Invalid dates selected',
        detail: 'The selected "To" date is set before the selected "From" date, no trips can be found'
      });
      return;
    }

    const selectedTrains = <Train[]>this.trainsSelectorForm.reportingForm.value.trains || [];
    const response: ResponseText = await this.reportingDataService.calculateTripsBySchedule(
      this.projectId,
      this.datePipe.transform(this.to, "yyyy-MM-dd") || '',
      this.datePipe.transform(this.from, "yyyy-MM-dd") || '',
      selectedTrains.map(train => train.relativeId).join(','))
    this.displayRecalculationMessage(response);
  }

  fetchTrainSchedule = async () => {
    const selectedTrains = <Train[]>this.trainsSelectorForm.reportingForm.value.trains || [];
    const response: ResponseText = await this.reportingDataService.fetchTrainSchedule(
      this.projectId,
      this.datePipe.transform(this.to, "yyyy-MM-dd") || '',
      selectedTrains.map(train => train.relativeId).join(','))
    this.displayRecalculationMessage(response);
  }

  displayRecalculationMessage(response: ResponseText): void {
    this.messageService.add({
      severity: response.success ? 'success' : 'error',
      summary: 'Recalculated trips',
      detail: response.message
    });
  }

  onExecuteQuery = () => {
    this.trainsSelectorForm.onSubmit();
  }

  exportToCsv = (trips: Trip[]) => {
    this.tripsOverviewService.exportTrips(trips);
  }

  setColumns(): void {
    //Get SQI columns from project Config
    if (this.sqiConfigurations) {
      let sqiColumns: any[] = [...new Set(this.sqiConfigurations.map(sqiConfiguration => {
        return {
          title: this.translate.instant('I18N_TRIP.KPI_' + sqiConfiguration.id + "_LABEL"),
          field: sqiConfiguration.label,
          type: 'number',
          width: '100px',
          disableSorting: false
        };
      }))];

      //Set the fixed first columns
      this.columns = [{
          title: this.translate.instant('I18N_TRIP.ID'),
          field: 'id',
          type: 'text',
          width: '60px'
        },{
        title: this.translate.instant('I18N_TRIP.SERVICE'),
        field: 'serviceId',
        type: 'text',
        width: '90px'
      }, {
        title: this.translate.instant('I18N_TRIP.TRAIN'),
        field: 'trainName',
        type: 'text',
        width: '70px'
      }, {
        title: this.translate.instant('I18N_TRIP.TRAIN_ID'),
        field: 'trainId',
        type: 'text',
        width: '60px'
      }, {
        title: this.translate.instant('I18N_TRIP.ROUTE'),
        field: 'trajectory',
        type: 'text',
        width: '90px'
      }, {
        title: this.translate.instant('I18N_TRIP.START'),
        field: 'departure',
        type: 'date',
        disableSearch: true,
        width: '90px'
      }, {
        title: this.translate.instant('I18N_TRIP.STOP'),
        field: 'arrival',
        type: 'date',
        disableSearch: true,
        width: '90px'
      }, {
        title: this.translate.instant('I18N_TRIP.DURATION'),
        field: 'duration',
        type: 'text',
        width: '90px',
        disableSearch: true
      }].concat(sqiColumns);
    }
  }

  //TODO Works? but don't like the way the state is changed
  tripDetailAnalysis = () => {
    if (this.selectedTrips && this.selectedTrips.length > 0) {
      this.selectedTrips.forEach(trip => this.tripOverviewFactory.addToList(trip.id, trip.trainName));
      this.$state.go('trip-analysis.chart', {
        projectId: this.selectedTrips[0].projectId,
        tripId: this.selectedTrips[0].id
      });
    }
  }
}
